import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { useToast } from "@components/Toast/ToastContext";
import { MdArrowBack, MdBusiness } from "react-icons/md";
import DeviceListTab from "./BrandSetup/DeviceListTab";
import SellerListTab from "./BrandSetup/SellerListTab";
import ServiceCenterTab from "./BrandSetup/ServiceCenterTab";
import TicketFormTab from "./BrandSetup/TicketFormTab";
import BrandSettingAdd from "./BrandSettingAdd";

const TABS = [
  { label: "Device List", key: "deviceList" },
  { label: "Seller List", key: "sellerList" },
  { label: "Service Center", key: "serviceCenter" },
  { label: "Ticket Form", key: "ticketForm" },
];

const BrandSetup = ({ protectedFetch, userInfo }) => {
  const { brandId } = useParams();
  const history = useHistory();
  const { showToast } = useToast();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(TABS[0].label);

  // Track which tabs have been activated (for lazy data loading)
  const [activatedTabs, setActivatedTabs] = useState({ [TABS[0].label]: true });

  const isNew = brandId === "new";

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    setLoading(true);
    protectedFetch(apiEndpoints.brand.detail(brandId), "GET", null, errorFunctions.checkResponseThenJson)
      .then((res) => {
        const data = res?.data || res;
        setBrand(data);
      })
      .catch(() => {
        showToast("Failed to load brand details", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [brandId]);

  const handleBrandCreated = useCallback((newBrandId) => {
    history.push(`/brand-setup/${newBrandId}`);
  }, [history]);

  const handleTabClick = useCallback((label) => {
    setActiveTab(label);
    setActivatedTabs((prev) => ({ ...prev, [label]: true }));
  }, []);

  if (loading) {
    return (
      <div className="page" style={{ textAlign: "center", paddingTop: 80 }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (isNew) {
    return (
      <div className="page">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <button
            onClick={() => history.push("/brand-settings")}
            className="btn btn-outline-secondary btn-sm"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              borderRadius: 8,
              padding: "6px 14px",
              fontWeight: 500,
            }}
          >
            <MdArrowBack size={16} /> Back
          </button>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              background: "#eef2ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MdBusiness style={{ fontSize: "1.3rem", color: "#4f46e5" }} />
          </div>
          <div>
            <h5 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700, color: "#1f2937" }}>
              New Brand Setup
            </h5>
            <p style={{ margin: "2px 0 0", fontSize: "0.8rem", color: "#6b7280" }}>
              Create a new brand first, then configure its settings
            </p>
          </div>
        </div>
        <BrandSettingAdd
          closeModal={() => history.push("/brand-settings")}
          retrieveBrands={() => {}}
          userInfo={userInfo}
          selectedBrand={null}
          protectedFetch={protectedFetch}
          onBrandCreated={handleBrandCreated}
        />
      </div>
    );
  }

  return (
    <div className="page">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <button
          onClick={() => history.push("/brand-settings")}
          className="btn btn-outline-secondary btn-sm"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            borderRadius: 8,
            padding: "6px 14px",
            fontWeight: 500,
          }}
        >
          <MdArrowBack size={16} /> Back
        </button>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            background: "#eef2ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MdBusiness style={{ fontSize: "1.3rem", color: "#4f46e5" }} />
        </div>
        <div>
          <h5 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700, color: "#1f2937" }}>
            {brand?.name || "Brand"} Setup
          </h5>
          <p style={{ margin: "2px 0 0", fontSize: "0.8rem", color: "#6b7280" }}>
            Configure devices, sellers, service centers, and ticket forms
          </p>
        </div>
      </div>

      <div className="tabs card">
        <ol className="tab-list card-header">
          {TABS.map((tab) => (
            <li
              key={tab.label}
              className={`tab-list-item${activeTab === tab.label ? " tab-list-active" : ""}`}
              onClick={() => handleTabClick(tab.label)}
              style={{ cursor: "pointer" }}
            >
              {tab.label}
            </li>
          ))}
        </ol>
        <div className="tab-content card-body">
          <div style={{ display: activeTab === "Device List" ? "block" : "none" }}>
            {activatedTabs["Device List"] && (
              <DeviceListTab brandId={brandId} brandName={brand?.name} brandSku={brand?.sku_code} protectedFetch={protectedFetch} />
            )}
          </div>
          <div style={{ display: activeTab === "Seller List" ? "block" : "none" }}>
            {activatedTabs["Seller List"] && (
              <SellerListTab brandId={brandId} protectedFetch={protectedFetch} />
            )}
          </div>
          <div style={{ display: activeTab === "Service Center" ? "block" : "none" }}>
            {activatedTabs["Service Center"] && (
              <ServiceCenterTab brandId={brandId} protectedFetch={protectedFetch} />
            )}
          </div>
          <div style={{ display: activeTab === "Ticket Form" ? "block" : "none" }}>
            <TicketFormTab brandId={brandId} protectedFetch={protectedFetch} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandSetup;
